"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { Bug, CheckCircle2, Lightbulb, MessageSquare, PenLine, type LucideIcon } from "lucide-react";
import { createClient } from "@/lib/supabase/client";
import { ThemeToggle } from "@/lib/theme";

const TYPES: { value: string; label: string; desc: string; icon: LucideIcon }[] = [
  { value: "feature", label: "Feature Request", desc: "Suggest something new",              icon: Lightbulb },
  { value: "bug",     label: "Bug Report",       desc: "Something isn't working",            icon: Bug },
  { value: "content", label: "Content Feedback", desc: "About exam questions or content",    icon: PenLine },
  { value: "general", label: "General Feedback", desc: "Anything else on your mind",         icon: MessageSquare },
];

export default function FeedbackPage() {
  const [type, setType] = useState("general");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const supabase = createClient();
    supabase.auth.getUser().then(async ({ data: { user } }) => {
      if (user?.email) setEmail(user.email);
    });
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!message.trim()) return;
    setSubmitting(true);
    setError(null);

    try {
      const res = await fetch("/api/user-feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type, message: message.trim(), email }),
      });
      if (res.ok) setSubmitted(true);
      else setError("Failed to send. Please try again.");
    } catch {
      setError("Network error — please check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link href="/dashboard" className="text-sm text-gray-400 hover:text-gray-700">← Dashboard</Link>
          <span className="text-gray-200">|</span>
          <span className="font-semibold text-gray-900">Send Feedback</span>
        </div>
        <ThemeToggle />
      </nav>

      <div className="max-w-lg mx-auto px-6 py-10">
        {submitted ? (
          <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
            <div className="mb-4 flex justify-center">
              <CheckCircle2 className="w-6 h-6 text-green-600" strokeWidth={1.75} aria-hidden="true" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Thanks for your feedback!</h2>
            <p className="text-sm text-gray-500 mb-6">
              We read every submission and use it to improve FullPracticeTests.
            </p>
            <div className="flex gap-3 justify-center">
              <Link
                href="/dashboard"
                className="text-sm bg-[var(--text-primary)] text-[var(--bg-primary)] px-5 py-2.5 rounded-lg hover:opacity-90 transition-opacity font-bold"
              >
                Back to Dashboard
              </Link>
              <button
                onClick={() => { setSubmitted(false); setMessage(""); setType("general"); }}
                className="text-sm text-gray-500 hover:text-gray-800 px-5 py-2.5 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
              >
                Send More Feedback
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 p-8">
            <h1 className="text-xl font-bold text-gray-900 mb-1">We&apos;d love your thoughts</h1>
            <p className="text-sm text-gray-500 mb-6">Your feedback directly shapes what we build next.</p>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Type selector */}
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 block">
                  What kind of feedback?
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {TYPES.map(t => (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => setType(t.value)}
                      className={`text-left px-3 py-2.5 rounded-lg border text-sm transition-colors
                        ${type === t.value
                          ? "border-blue-400 bg-blue-50 text-blue-900"
                          : "border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50"}`}
                    >
                      <div className="font-medium flex items-center gap-2">
                        <t.icon className="w-4 h-4" strokeWidth={1.75} aria-hidden="true" />
                        {t.label}
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">{t.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 block">
                  Your message
                </label>
                <textarea
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  required
                  rows={5}
                  placeholder="Tell us what's on your mind…"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
                />
                <div className="text-xs text-gray-400 mt-1 text-right">{message.length} chars</div>
              </div>

              {/* Email (pre-filled if logged in, editable) */}
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 block">
                  Your email <span className="text-gray-400 font-normal normal-case">(optional — if you&apos;d like a reply)</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>

              {error && <p className="text-red-500 text-sm">{error}</p>}

              <button
                type="submit"
                disabled={submitting || !message.trim()}
                className="w-full bg-[var(--text-primary)] text-[var(--bg-primary)] font-bold py-3 rounded-lg hover:opacity-90 disabled:opacity-40 transition-opacity"
              >
                {submitting ? "Sending…" : "Send Feedback →"}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
